import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight } from "lucide-react";
import DeleteAccountFlow from "@/components/DeleteAccountFlow";
import SEOHead from "@/components/SEOHead";

const SettingsAccount = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [stats, setStats] = useState({
    contactCount: 0,
    messagesGenerated: 0,
    reflectionLikes: 0,
    accountAgeMonths: 0,
  });

  useEffect(() => {
    if (!user) return;
    const fetchStats = async () => {
      const [contactsRes, messagesRes, likesRes, profileRes] = await Promise.all([
        supabase.from("contacts").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("message_generation_logs").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("reflection_engagement").select("*", { count: "exact", head: true }).eq("user_id", user.id).eq("liked", true),
        supabase.from("profiles").select("created_at").eq("user_id", user.id).single(),
      ]);

      const accountAgeMonths = profileRes.data?.created_at
        ? Math.max(0, Math.floor((Date.now() - new Date(profileRes.data.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30)))
        : 0;

      setStats({
        contactCount: contactsRes.count || 0,
        messagesGenerated: messagesRes.count || 0,
        reflectionLikes: likesRes.count || 0,
        accountAgeMonths,
      });
    };
    fetchStats();
  }, [user]);

  return (
    <>
      <SEOHead title="Account — Dearestly." description="Manage your Dearestly. account and data." path="/settings/account" noindex />
      <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 md:px-6 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-page-title text-foreground">Account</h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 md:px-6 py-8 pb-24 animate-fade-in">
        <button
          onClick={() => setDrawerOpen(true)}
          className="w-full flex items-center justify-between card-premium p-5 hover:bg-muted/50 transition-colors text-left gap-3"
        >
          <div>
            <p className="text-sm text-foreground">Delete account</p>
            <p className="text-xs text-muted-foreground/70 mt-0.5">
              Permanently remove your account and data
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </button>
      </main>

      <DeleteAccountFlow open={drawerOpen} onOpenChange={setDrawerOpen} stats={stats} />
    </div>
  </>
  );
};

export default SettingsAccount;
